import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Layout from "./layout";
import { doFetch } from "../redux/api/actions";
import { ADMIN_GET_TOURS, ADMIN_GET_USERS, ME } from "../redux/api/constants";

function Admin() {
  const dispatch = useDispatch();

  const accessToken = useSelector((state) => state.Auth.accessToken);
  const me = useSelector((state) => state.API.me);
  const loading = useSelector((state) => state.API.loading);
  const error = useSelector((state) => state.API.error);
  const adminUsers = useSelector((state) => state.API.adminUsers);
  const adminTours = useSelector((state) => state.API.adminTours);

  const [userQuery, setUserQuery] = useState("");
  const [tourQuery, setTourQuery] = useState("");

  useEffect(() => {
    if (accessToken && !me) {
      dispatch(doFetch(ME, "users/me", accessToken));
    }
  }, [dispatch, accessToken, me]);

  useEffect(() => {
    if (!accessToken) return;
    if (!me?.is_superuser) return;

    dispatch(doFetch(ADMIN_GET_USERS, "users", accessToken));
    dispatch(doFetch(ADMIN_GET_TOURS, "tours", accessToken));
  }, [dispatch, accessToken, me?.is_superuser]);

  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return adminUsers || [];
    return (adminUsers || []).filter((u) => {
      const email = (u?.email || "").toLowerCase();
      const id = String(u?.id || "").toLowerCase();
      return email.includes(q) || id.includes(q);
    });
  }, [adminUsers, userQuery]);

  const filteredTours = useMemo(() => {
    const q = tourQuery.trim().toLowerCase();
    if (!q) return adminTours || [];
    return (adminTours || []).filter((t) => {
      const title = (t?.title || "").toLowerCase();
      const id = String(t?.id || "").toLowerCase();
      return title.includes(q) || id.includes(q);
    });
  }, [adminTours, tourQuery]);

  return (
    <Layout selected="admin">
      <nav aria-label="breadcrumb" className="breadcrumb-nav">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item active">Admin</li>
        </ol>
      </nav>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="mb-1">Admin</h1>
          <div className="text-muted small">
            Users: <strong>{adminUsers?.length || 0}</strong> · Tours:{" "}
            <strong>{adminTours?.length || 0}</strong>
          </div>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-dark"
            onClick={() => {
              if (!accessToken || !me?.is_superuser) return;
              dispatch(doFetch(ADMIN_GET_USERS, "users", accessToken));
              dispatch(doFetch(ADMIN_GET_TOURS, "tours", accessToken));
            }}
            disabled={!accessToken || !me?.is_superuser || loading}
          >
            Refresh
          </button>
        </div>
      </div>

      {!accessToken ? (
        <div className="alert alert-warning">
          Please <Link to="/signin">sign in</Link> to access admin tools.
        </div>
      ) : me && !me.is_superuser ? (
        <div className="alert alert-danger">
          Access denied. This page is for admins only.
        </div>
      ) : null}

      {error ? (
        <div className="alert alert-danger">
          <div className="fw-bold mb-1">Error</div>
          <div className="small">{String(error)}</div>
        </div>
      ) : null}

      <div className="row g-4">
        <div className="col-12 col-lg-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="card-title mb-0">All Users</h3>
                <span className="badge bg-secondary">
                  {filteredUsers.length}
                </span>
              </div>

              <input
                className="form-control mb-3"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder="Search by email or id…"
                aria-label="Search users"
              />

              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th className="text-nowrap">Superuser</th>
                      <th className="text-nowrap">Active</th>
                      <th className="text-nowrap">Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id || u.email}>
                        <td className="text-break">
                          <div className="fw-semibold">{u.email}</div>
                          <div className="text-muted small">{u.id}</div>
                        </td>
                        <td>{u.is_superuser ? "Yes" : "No"}</td>
                        <td>{u.is_active ? "Yes" : "No"}</td>
                        <td>{u.is_verified ? "Yes" : "No"}</td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-muted">
                          No users found.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="card-title mb-0">All Tours</h3>
                <span className="badge bg-secondary">
                  {filteredTours.length}
                </span>
              </div>

              <input
                className="form-control mb-3"
                value={tourQuery}
                onChange={(e) => setTourQuery(e.target.value)}
                placeholder="Search by title or id…"
                aria-label="Search tours"
              />

              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th className="text-nowrap">Price</th>
                      <th className="text-nowrap">Max size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTours.map((t) => (
                      <tr key={t.id}>
                        <td className="text-break">
                          <div className="fw-semibold">{t.title}</div>
                          <div className="text-muted small">{t.id}</div>
                        </td>
                        <td>{t.price != null ? `$${t.price}` : "-"}</td>
                        <td>{t.max_tour_size ?? "-"}</td>
                      </tr>
                    ))}
                    {filteredTours.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-muted">
                          No tours found.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Admin;

